import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  EMedicalViews,
  IFetchHealthProceduresParams,
  IFilterTag,
  IHealthProcedures,
  TimelineGeneralPeriodType,
  createDateFilterTagWith,
  createSearchTermTagWith,
  createTypeFiltersTagWith
} from '~/interfaces'
import { FilterType } from '~/components/shared'
import { useFetchHealthProcedures } from '~/hooks'
import { HealthProcedureVariantProps } from '~/constants'
import { identifyInProgressEvents, transformDate } from '~/utils'
import { removeUnsupportedEvents } from '~/services'

interface OncologyHealthProceduresProviderProps {
  children: React.ReactNode
}

export interface IOncologyHealthProceduresContextHelpers {
  isLoadingTimeline: boolean
  errorTimeline: string
}

interface ISelectedPeriod {
  period: TimelineGeneralPeriodType
  previousPeriod: TimelineGeneralPeriodType
}

const defaultValue = {
  healthProcedures: [] as IHealthProcedures[],
  filterTags: [] as IFilterTag[],
  selectedPeriod: { period: 'years', previousPeriod: 'none' } as ISelectedPeriod
}

export interface IOncologyHealthProceduresContextProps extends IOncologyHealthProceduresContextHelpers {
  displayedHealthProcedures: IHealthProcedures[]
  getHealthProcedures: (params: IFetchHealthProceduresParams) => void

  selectedPeriod: ISelectedPeriod
  updateSelectedPeriod: (newSelectedPeriod: ISelectedPeriod) => void

  hasDateFiltersActive: boolean
  hasTypeFiltersActive: boolean
  dateFiltersActive: Date[]

  typeFiltersActive: string[]
  openConductCardList: boolean
  openOmicsCardList: boolean

  handleFilterHealthProceduresPerType: (types: FilterType[]) => void
  initializeSidePanel: () => void
  setOpenConductCardList: Dispatch<SetStateAction<boolean>>
  setOpenOmicsCardList: Dispatch<SetStateAction<boolean>>

  updateSidePanelEvents: (events: IHealthProcedures[]) => void
  removeFilterTag: (tag: IFilterTag) => void

  filterHealthProceduresByTerm: (term: IFilterTag) => void
  handleFilterHealthProceduresPerDate: (rangeDates: Date[]) => void

  filterTags: IFilterTag[]
  selectMedicalEventForDetailing: (healthProcedures: IHealthProcedures | null) => void

  sidePanelEvents: IHealthProcedures[]
  modalHealthProcedure: IHealthProcedures | null

  closeHealthProcedureDetails: () => void
  resetFilterTags: () => void

  resetSidePanelEvents: () => void
  showHealthProcedureDetails: () => boolean
}

const OncologyHealthProceduresContext = React.createContext<IOncologyHealthProceduresContextProps>(
  {} as IOncologyHealthProceduresContextProps
)

function OncologyHealthProceduresProvider({ children }: OncologyHealthProceduresProviderProps): JSX.Element {
  const [selectedPeriod, setSelectedPeriod] = useState<ISelectedPeriod>(defaultValue.selectedPeriod)
  const [healthProceduresParams, setHealthProceduresParams] = useState<IFetchHealthProceduresParams | null>(null)
  const [typeFiltersActive, setTypeFiltersActive] = useState<string[]>([])
  const [filterTags, setFilterTags] = useState<IFilterTag[]>(defaultValue.filterTags)
  const [sidePanelEvents, setSidePanelEvents] = useState<IHealthProcedures[]>([])
  const [dateFiltersActive, setDateFiltersActive] = useState<Date[]>([])
  const [modalHealthProcedure, setModalHealthProcedure] = useState<IHealthProcedures | null>(null)

  const [openConductCardList, setOpenConductCardList] = useState<boolean>(false)
  const [openOmicsCardList, setOpenOmicsCardList] = useState<boolean>(false)

  const updateSidePanelEvents = useCallback((events: IHealthProcedures[]) => setSidePanelEvents(events), [])
  const [displayedHealthProcedures, setDisplayedHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )

  const hasTypeFiltersActive: boolean = typeFiltersActive.length > 0
  const hasDateFiltersActive: boolean = dateFiltersActive.length > 0

  const {
    data: healthProcedures,
    isLoading: isLoadingTimeline,
    error: errorTimeline
  } = useFetchHealthProcedures(healthProceduresParams, EMedicalViews.ONCOLOGY)
  const resetSidePanelEvents = useCallback(() => {
    if (healthProcedures?.length) updateSidePanelEvents(healthProcedures)
  }, [updateSidePanelEvents, healthProcedures])

  const updateDateFilterTags = useCallback(
    (rangeDates: Date[]): void => {
      const tagsWithoutDateFilter = filterTags.filter(tag => tag.type !== 'dateFilter')
      const isNotAValidRangeOfDates = rangeDates.length <= 1
      if (isNotAValidRangeOfDates) {
        setFilterTags(tagsWithoutDateFilter)
        return
      }

      const formattedDates = rangeDates.map(date => transformDate({ date }))
      const tagDescription = formattedDates.join(' - ')
      setFilterTags([...tagsWithoutDateFilter, createDateFilterTagWith(tagDescription)])
    },
    [filterTags]
  )

  const getHealthProcedures = useCallback((params: IFetchHealthProceduresParams): void => {
    setHealthProceduresParams(params)
  }, [])

  const removeTypeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      const newTypeFiltersActive = typeFiltersActive.filter(type => {
        const tagTypes = tag.id.split('-')
        return !tagTypes.includes(type)
      })
      setTypeFiltersActive(newTypeFiltersActive)
    },
    [typeFiltersActive]
  )

  const showHealthProcedureDetails = (): boolean => {
    return !!modalHealthProcedure
  }

  const updateSelectedPeriod = (newSelectedPeriod: ISelectedPeriod): void => {
    setSelectedPeriod({ ...newSelectedPeriod })
  }

  const initializeSidePanel = useCallback(() => {
    resetSidePanelEvents()
  }, [resetSidePanelEvents])

  const handleFilterHealthProceduresPerDate = useCallback(
    (rangeDates: Date[]) => {
      const isAValidRangeOfDates = rangeDates.length !== 1

      if (isAValidRangeOfDates) {
        setDateFiltersActive(rangeDates)
        updateDateFilterTags(rangeDates)
        initializeSidePanel()
        setOpenOmicsCardList(true)
        setOpenConductCardList(true)
      }
    },
    [initializeSidePanel, updateDateFilterTags]
  )

  const removeDateFilterTag = (): void => {
    setDateFiltersActive([])
  }

  const removeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      setFilterTags(existingTags => existingTags.filter(ft => ft.id !== tag.id))

      switch (tag.type) {
        case 'dateFilter':
          return removeDateFilterTag()
        case 'typeFilter':
          return removeTypeFilterTag(tag)
      }
    },
    [removeTypeFilterTag]
  )

  const updateTypeFilterTags = useCallback((filters: FilterType[]): void => {
    const tagsOfTypeFilters = filters.map(f => {
      const filterColor = HealthProcedureVariantProps[f.type[0]].color
      return createTypeFiltersTagWith(f.text, filterColor, f.type)
    })

    setFilterTags(existingTags => {
      const existentTags = existingTags.filter(tag => tag.type !== 'typeFilter')
      return [...existentTags, ...tagsOfTypeFilters]
    })
  }, [])

  const handleFilterHealthProceduresPerType = useCallback(
    (typeFilters: FilterType[]) => {
      const activeTypes = typeFilters.flatMap(f => f.type)
      setTypeFiltersActive(activeTypes)
      updateTypeFilterTags(typeFilters)
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
    },
    [updateTypeFilterTags, initializeSidePanel]
  )

  const filterHealthProceduresByTerm = useCallback(
    (term: IFilterTag) => {
      const tagByTerm = createSearchTermTagWith(term.text)
      setFilterTags(existingTags => [...existingTags, tagByTerm])
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
    },
    [initializeSidePanel]
  )

  const selectMedicalEventForDetailing = (healthProcedures: IHealthProcedures | null) => {
    setModalHealthProcedure(healthProcedures)
  }

  const closeHealthProcedureDetails = (): void => {
    setModalHealthProcedure(null)
  }

  const resetFilterTags = useCallback((): void => {
    setFilterTags(defaultValue.filterTags)

    setTypeFiltersActive([])
    removeDateFilterTag()
  }, [])

  const treatFetchedHealthProcedures = useCallback((healthProcedures: IHealthProcedures[]) => {
    const validEvents = removeUnsupportedEvents(healthProcedures)
    const inProgressEvents = identifyInProgressEvents(validEvents)
    return inProgressEvents
  }, [])

  useEffect(() => {
    if (displayedHealthProcedures.length > 0) {
      initializeSidePanel()
    }
  }, [displayedHealthProcedures, initializeSidePanel])

  useEffect(() => {
    if (healthProcedures) {
      setDisplayedHealthProcedures(treatFetchedHealthProcedures(healthProcedures))
    }
  }, [healthProcedures, treatFetchedHealthProcedures])

  return (
    <OncologyHealthProceduresContext.Provider
      value={{
        errorTimeline: errorTimeline?.message ?? '',
        isLoadingTimeline,
        selectedPeriod,
        updateSelectedPeriod,
        handleFilterHealthProceduresPerType,
        hasTypeFiltersActive,
        typeFiltersActive,
        initializeSidePanel,
        setOpenOmicsCardList,
        openConductCardList,
        setOpenConductCardList,
        openOmicsCardList,
        updateSidePanelEvents,
        removeFilterTag,
        displayedHealthProcedures,
        filterHealthProceduresByTerm,
        hasDateFiltersActive,
        dateFiltersActive,
        handleFilterHealthProceduresPerDate,
        filterTags,
        selectMedicalEventForDetailing,
        sidePanelEvents,
        closeHealthProcedureDetails,
        modalHealthProcedure,
        getHealthProcedures,
        resetFilterTags,
        resetSidePanelEvents,
        showHealthProcedureDetails
      }}
    >
      {children}
    </OncologyHealthProceduresContext.Provider>
  )
}

const useOncologyHealthProcedures = (): IOncologyHealthProceduresContextProps => {
  return React.useContext(OncologyHealthProceduresContext)
}
export { OncologyHealthProceduresContext, OncologyHealthProceduresProvider, useOncologyHealthProcedures }
